/* global BigInt */

import React, { useEffect, useId, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  formatMinorUnits,
  formatQuantity,
  moneyMinorValue,
  quantityIntegerValue
} from '../reportFormatters';
import { createChartTimeline } from '../reportChartTimeline';
import { REPORT_SERIES_COLORS } from '../reportChartPalette';

const BASIS_MESSAGE_KEYS = {
  with_vat: 'withVat',
  without_vat: 'withoutVat'
};
const CHART_HEIGHT = 240;
const PLOT_BOUNDS = { left: 12, right: 988, top: 12, bottom: 228 };
const OVERVIEW_CHART_HEIGHT = 420;
const OVERVIEW_PLOT_BOUNDS = { left: 12, right: 988, top: 12, bottom: 408 };
const TICK_COUNT = 5;

function buildOverviewTickDomain(observedMaximum, usesBigInt) {
  const zero = usesBigInt ? 0n : 0;
  const one = usesBigInt ? 1n : 1;
  if (observedMaximum <= zero) {
    return { maximum: usesBigInt ? 4n : 4, step: one, tickCount: 5 };
  }

  const padding = usesBigInt
    ? ((observedMaximum + 9n) / 10n > 1n ? (observedMaximum + 9n) / 10n : 1n)
    : Math.max(1, observedMaximum * 0.1);
  const target = observedMaximum + padding;
  const multipliers = usesBigInt ? [1n, 2n, 5n] : [1, 2, 5];
  let selected = null;
  let power = one;

  while (power <= target * (usesBigInt ? 10n : 10)) {
    for (let index = 0; index < multipliers.length; index += 1) {
      const multiplier = multipliers[index];
      const step = multiplier * power;
      const intervals = usesBigInt
        ? Number((target + step - 1n) / step)
        : Math.ceil(target / step);
      if (intervals >= 3 && intervals <= 5) {
        const maximum = step * (usesBigInt ? BigInt(intervals) : intervals);
        if (!selected || maximum < selected.maximum || (
          maximum === selected.maximum && intervals > selected.intervals
        )) {
          selected = { intervals, maximum, step };
        }
      }
    }
    power *= usesBigInt ? 10n : 10;
  }

  if (!selected) {
    const step = usesBigInt
      ? (target + 4n) / 5n
      : Math.ceil(target / 5);
    selected = {
      intervals: 5,
      maximum: step * (usesBigInt ? 5n : 5),
      step
    };
  }

  return {
    maximum: selected.maximum,
    step: selected.step,
    tickCount: selected.intervals + 1
  };
}

function normalizeNumber(value) {
  return Number(Number(value).toFixed(6));
}

function buildChartScale(rawValues, bounds = PLOT_BOUNDS, options = {}) {
  const usesBigInt = rawValues.some(value => typeof value === 'bigint');
  const values = usesBigInt
    ? rawValues.map(value => typeof value === 'bigint' ? value : BigInt(Math.trunc(value || 0)))
    : rawValues.map(value => Number(value) || 0);
  const zero = usesBigInt ? 0n : 0;
  const observedMaximum = values.reduce((current, value) => value > current ? value : current, zero);
  let minimum = values.reduce((current, value) => value < current ? value : current, zero);
  let maximum = values.reduce((current, value) => value > current ? value : current, zero);
  let tickCount = TICK_COUNT;
  let tickStep = null;

  if (minimum < zero && maximum > zero) {
    const negativeExtent = usesBigInt ? -minimum : Math.abs(minimum);
    let extent = negativeExtent > maximum ? negativeExtent : maximum;
    const minimumExtent = usesBigInt ? 2n : 1;
    extent = extent < minimumExtent ? minimumExtent : extent;
    minimum = -extent;
    maximum = extent;
  } else if (minimum === maximum) {
    maximum = usesBigInt ? 4n : 1;
  } else if (usesBigInt && maximum > zero && maximum < 4n) {
    maximum = 4n;
  } else if (usesBigInt && minimum < zero && minimum > -4n) {
    minimum = -4n;
  }

  if (options.headroom && minimum >= zero) {
    const overviewDomain = buildOverviewTickDomain(observedMaximum, usesBigInt);
    maximum = overviewDomain.maximum;
    tickCount = overviewDomain.tickCount;
    tickStep = overviewDomain.step;
  }

  const range = maximum - minimum;
  const ratio = value => {
    if (usesBigInt) {
      return Number(((value - minimum) * 1000000n) / range) / 1000000;
    }
    return (value - minimum) / range;
  };
  const yForValue = value => normalizeNumber(
    bounds.bottom - ratio(value) * (bounds.bottom - bounds.top)
  );
  const ticks = Array.from({ length: tickCount }, (_, index) => {
    const value = tickStep !== null
      ? minimum + (tickStep * (usesBigInt ? BigInt(index) : index))
      : usesBigInt
        ? minimum + ((range * BigInt(index)) / BigInt(tickCount - 1))
        : normalizeNumber(minimum + ((range * index) / (tickCount - 1)));
    return {
      isZero: value === zero,
      value,
      y: yForValue(value)
    };
  });

  return {
    maximum,
    minimum,
    ticks,
    usesBigInt,
    yForValue
  };
}

function buildPoints(series, valueAccessor, scale) {
  return series.reduce((points, bucket, index) => {
    const value = valueAccessor(bucket);
    if (value === null || value === undefined) {
      return points;
    }
    const x = series.length === 1
      ? (PLOT_BOUNDS.left + PLOT_BOUNDS.right) / 2
      : PLOT_BOUNDS.left + ((index / (series.length - 1)) * (PLOT_BOUNDS.right - PLOT_BOUNDS.left));
    return [...points, { bucket, x, y: scale.yForValue(value) }];
  }, []);
}

function sampleAxisPoints(points, maximumLabels = 5) {
  const priority = point => (point.quarterBoundary ? 2 : 0) + (point.domainEndpoint ? 1 : 0);
  const byLabel = Array.from(points.reduce((unique, point) => {
    const key = point.bucket.label || point.bucket.key;
    const existing = unique.get(key);
    if (!existing || priority(point) > priority(existing)) unique.set(key, point);
    return unique;
  }, new Map()).values()).sort((first, second) => first.x - second.x);
  const uniquePoints = byLabel.reduce((result, point) => {
    const previous = result[result.length - 1];
    if (previous && Math.abs(previous.x - point.x) < 0.5) {
      if (priority(point) > priority(previous)) result[result.length - 1] = point;
      return result;
    }
    return [...result, point];
  }, []);
  if (uniquePoints.length <= maximumLabels) return uniquePoints;
  const boundaries = uniquePoints.filter(point => point.quarterBoundary || point.domainEndpoint);
  const sample = (candidates, count) => {
    if (count <= 0 || candidates.length === 0) return [];
    if (candidates.length <= count) return candidates;
    if (count === 1) return [candidates[Math.floor((candidates.length - 1) / 2)]];
    return Array.from({ length: count }, (_, index) => (
      candidates[Math.round((index * (candidates.length - 1)) / (count - 1))]
    )).filter((point, index, sampled) => (
      index === 0 || point.bucket.key !== sampled[index - 1].bucket.key
    ));
  };
  if (boundaries.length === 0) return sample(uniquePoints, maximumLabels);
  const sampledPoints = sample(
    uniquePoints.filter(point => !point.quarterBoundary && !point.domainEndpoint),
    Math.max(0, maximumLabels - boundaries.length)
  );
  return [...boundaries, ...sampledPoints].sort((first, second) => first.x - second.x);
}

function bucketDateRange(bucket) {
  if (!bucket || (!bucket.date_from && !bucket.date_to)) {
    return '';
  }
  if (bucket.date_from === bucket.date_to) {
    return bucket.date_from || '';
  }
  return `${bucket.date_from || '—'} – ${bucket.date_to || '—'}`;
}

function ComparisonControl({ checked, onChange }) {
  return (
    <label className="report-comparison-control">
      <input
        checked={checked}
        onChange={() => {}}
        onClick={() => onChange(!checked)}
        type="checkbox"
      />
      <i aria-hidden="true" className="report-comparison-control__line"/>
      <span><FormattedMessage id="components.pages.reports.summary.comparisonSeries"/></span>
    </label>
  );
}

function TrendChart({
  annual,
  axisLabel,
  className,
  color,
  comparisonControl = true,
  comparisonEnabled,
  onComparisonChange,
  comparisonPeriod,
  comparisonSeries = [],
  currentPeriod,
  formatTick,
  overview = false,
  pointTitle,
  secondaryColor,
  secondaryLabel,
  secondaryPointTitle,
  secondaryValueAccessor,
  series,
  showDateRange,
  valueAccessor,
  scaleFallback,
  xAxisLabel
}) {
  const [internalComparisonEnabled, setInternalComparisonEnabled] = useState(true);
  const [activePoint, setActivePoint] = useState(null);
  const [measuredOverviewHeight, setMeasuredOverviewHeight] = useState(OVERVIEW_CHART_HEIGHT);
  const svgRef = useRef(null);
  const tooltipId = useId();
  const chartHeight = overview ? measuredOverviewHeight : CHART_HEIGHT;
  const plotBounds = overview
    ? { ...OVERVIEW_PLOT_BOUNDS, bottom: chartHeight - 12 }
    : PLOT_BOUNDS;
  useEffect(() => {
    if (!overview || !svgRef.current) return undefined;
    const svg = svgRef.current;
    const measure = () => {
      const bounds = svg.getBoundingClientRect();
      if (bounds.width > 0 && bounds.height > 0) {
        const nextHeight = Math.round((1000 * bounds.height) / bounds.width);
        setMeasuredOverviewHeight(current => current === nextHeight ? current : nextHeight);
      }
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(svg);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [overview]);
  const showComparison = typeof comparisonEnabled === 'boolean'
    ? comparisonEnabled
    : internalComparisonEnabled;
  const timeline = createChartTimeline({
    annual,
    comparisonPeriod,
    comparisonSeries,
    currentPeriod,
    currentSeries: series,
    left: plotBounds.left,
    right: plotBounds.right
  });
  const comparisonAvailable = timeline.comparisonPoints.length > 0;
  const comparisonVisible = comparisonAvailable && showComparison;
  const seriesColor = color || (className
    ? REPORT_SERIES_COLORS.signatures
    : REPORT_SERIES_COLORS.total);
  const currentValues = timeline.currentPoints
    .map(point => valueAccessor(point.bucket))
    .filter(value => value !== null && value !== undefined);
  const secondaryValues = secondaryValueAccessor
    ? timeline.currentPoints.map(point => secondaryValueAccessor(point.bucket))
      .filter(value => value !== null && value !== undefined)
    : [];
  const comparisonValues = comparisonVisible
    ? timeline.comparisonPoints
      .map(point => valueAccessor(point.bucket))
      .filter(value => value !== null && value !== undefined)
    : [];
  const values = [...currentValues, ...secondaryValues, ...comparisonValues];
  const scale = buildChartScale(
    values.length > 0 ? values : [scaleFallback],
    plotBounds,
    { headroom: overview }
  );
  const pointsFor = (source, accessor = valueAccessor) => source.reduce((points, point) => {
    const value = accessor(point.bucket);
    return value === null || value === undefined
      ? points
      : [...points, { ...point, y: scale.yForValue(value) }];
  }, []);
  const points = pointsFor(timeline.currentPoints);
  const secondaryPoints = secondaryValueAccessor
    ? pointsFor(timeline.currentPoints, secondaryValueAccessor)
    : [];
  const comparisonPoints = comparisonVisible ? pointsFor(timeline.comparisonPoints) : [];
  const xTicks = sampleAxisPoints(
    timeline.axisPoints,
    annual || timeline.bands.length > 1 ? timeline.axisPoints.length : 5
  );
  const zero = scale.usesBigInt ? 0n : 0;
  const crossesZero = scale.minimum < zero && scale.maximum > zero;
  const handleComparisonChange = () => {
    const checked = !showComparison;
    if (typeof comparisonEnabled !== 'boolean') {
      setInternalComparisonEnabled(checked);
    }
    if (onComparisonChange) {
      onComparisonChange(checked);
    }
  };
  const pointInteractionProps = (point, source, labelBuilder = pointTitle) => {
    const key = `${source}:${point.bucket.key}`;
    const label = labelBuilder(point.bucket);
    const activate = event => {
      const targetBounds = event.currentTarget.getBoundingClientRect();
      const canvas = event.currentTarget.closest('.report-trend-panel__canvas');
      const canvasBounds = canvas && canvas.getBoundingClientRect();
      const measured = canvasBounds && canvasBounds.width > 0 && canvasBounds.height > 0;
      const renderedX = measured
        ? targetBounds.left + (targetBounds.width / 2) - canvasBounds.left
        : null;
      const renderedY = measured
        ? targetBounds.top + (targetBounds.height / 2) - canvasBounds.top
        : null;
      setActivePoint({
        key,
        label,
        left: measured ? `${renderedX}px` : `${(point.x / 1000) * 100}%`,
        ratioX: measured ? renderedX / canvasBounds.width : point.x / 1000,
        ratioY: measured ? renderedY / canvasBounds.height : point.y / chartHeight,
        top: measured ? `${renderedY}px` : `${(point.y / chartHeight) * 100}%`
      });
    };
    return {
      'aria-describedby': activePoint && activePoint.key === key ? tooltipId : undefined,
      'aria-label': label,
      focusable: 'true',
      onBlur: () => setActivePoint(current => current && current.key === key ? null : current),
      onFocus: activate,
      onMouseEnter: activate,
      onMouseLeave: () => setActivePoint(current => current && current.key === key ? null : current),
      tabIndex: 0
    };
  };

  return (
    <div className={`report-trend-panel__chart report-trend-panel__chart--${showDateRange ? 'dated' : 'compact'}`}>
      {comparisonAvailable && (
        <div className="report-trend-panel__comparison-row">
          <div className="report-chart-legend">
            <span><i className="current"/><FormattedMessage id="components.pages.reports.summary.currentSeries"/></span>
            {comparisonVisible && <span><i className="comparison"/><FormattedMessage id="components.pages.reports.summary.comparisonSeries"/></span>}
          </div>
          {comparisonControl && (
            <ComparisonControl checked={showComparison} onChange={handleComparisonChange}/>
          )}
        </div>
      )}
      <div className="report-trend-panel__plot-frame">
        <ol aria-label={axisLabel} className="report-trend-panel__y-axis">
          {scale.ticks.map((tick, index) => (
            <li
              className={crossesZero && tick.isZero ? 'report-trend-panel__y-tick--zero' : ''}
              key={`${String(tick.value)}:${index}`}
              style={{ top: `${(tick.y / chartHeight) * 100}%` }}
            >
              {formatTick(tick.value)}
            </li>
          ))}
        </ol>
        <div className="report-trend-panel__canvas">
          {secondaryPoints.length > 0 && (
            <div className="report-trend-panel__series-legend">
              <span><i style={{ backgroundColor: seriesColor }}/>{axisLabel}</span>
              <span><i style={{ backgroundColor: secondaryColor }}/>{secondaryLabel}</span>
            </div>
          )}
          <svg aria-label={axisLabel} preserveAspectRatio={overview ? 'xMidYMid meet' : 'none'} ref={svgRef} role="img" viewBox={`0 0 1000 ${chartHeight}`}>
        <g aria-hidden="true" className="report-trend-panel__quarter-bands">
          {timeline.bands.map(band => (
            <g data-quarter={band.key} key={band.key}>
              <rect
                className={band.alternate ? 'alternate' : ''}
                height={plotBounds.bottom - plotBounds.top}
                width={band.width}
                x={band.start}
                y={plotBounds.top}
              />
              <text x={band.start + 7} y={plotBounds.top + 13}>{band.key.split('-')[1]}</text>
            </g>
          ))}
        </g>
        <g aria-hidden="true" className="report-trend-panel__grid">
          {scale.ticks.map((tick, index) => (
            <line
              className={crossesZero && tick.isZero ? 'report-trend-panel__grid-line--zero' : ''}
              data-testid="trend-grid-line"
              key={`${String(tick.value)}:${index}`}
              x1={plotBounds.left}
              x2={plotBounds.right}
              y1={tick.y}
              y2={tick.y}
            />
          ))}
        </g>
        <polyline
          className={`report-trend-panel__line${className ? ` ${className}` : ''}`}
          fill="none"
          points={points.map(point => `${point.x},${point.y}`).join(' ')}
          style={{ stroke: seriesColor }}
        />
        {points.map(point => (
          <circle
            {...pointInteractionProps(point, 'current')}
            className={className ? 'report-trend-panel__point--quantity' : ''}
            cx={point.x}
            cy={point.y}
            key={point.bucket.key}
            r="7"
            style={{ stroke: seriesColor }}
          >
            <title>{pointTitle(point.bucket)}</title>
          </circle>
        ))}
        {secondaryPoints.length > 0 && (
          <g className="secondary">
            <polyline
              className="report-trend-panel__line report-trend-panel__line--secondary"
              fill="none"
              points={secondaryPoints.map(point => `${point.x},${point.y}`).join(' ')}
              style={{ stroke: secondaryColor }}
            />
            {secondaryPoints.map(point => (
              <circle
                {...pointInteractionProps({ ...point, bucket: point.bucket }, 'secondary', secondaryPointTitle)}
                className="report-trend-panel__point--secondary"
                cx={point.x}
                cy={point.y}
                key={`secondary:${point.bucket.key}`}
                r="7"
                style={{ stroke: secondaryColor }}
              >
                <title>{secondaryPointTitle(point.bucket)}</title>
              </circle>
            ))}
          </g>
        )}
        {comparisonVisible && (
          <g className="comparison">
            <polyline
              className={`report-trend-panel__line${className ? ` ${className}` : ''}`}
              fill="none"
              points={comparisonPoints.map(point => `${point.x},${point.y}`).join(' ')}
              style={{ stroke: seriesColor }}
            />
            {comparisonPoints.map(point => (
              <circle
                {...pointInteractionProps(point, 'comparison')}
                className={className ? 'report-trend-panel__point--quantity' : ''}
                cx={point.x}
                cy={point.y}
                key={`comparison:${point.bucket.key}`}
                r="7"
                style={{ stroke: seriesColor }}
              >
                <title>{pointTitle(point.bucket)}</title>
              </circle>
            ))}
          </g>
        )}
          </svg>
          {activePoint && (
            <div
              className="report-trend-panel__tooltip"
              data-horizontal={activePoint.ratioX < 0.18 ? 'start' : activePoint.ratioX > 0.82 ? 'end' : 'center'}
              data-placement={activePoint.ratioY < 0.18 ? 'below' : 'above'}
              id={tooltipId}
              role="tooltip"
              style={{
                left: activePoint.left,
                top: activePoint.top
              }}
            >
              {activePoint.label}
            </div>
          )}
          <ol aria-label={xAxisLabel} className="report-trend-panel__x-axis">
            {xTicks.map((point, index) => {
              const label = point.bucket.label || point.bucket.key || '—';
              const range = point.axisMonth || point.annualMonth ? '' : bucketDateRange(point.bucket);
              const accessibleLabel = range ? `${label}: ${range}` : label;
              return (
                <li
                  aria-label={accessibleLabel}
                  className={[
                    point.axisLabelAnchor === 'center' ? 'report-trend-panel__x-tick--centered' : '',
                    point.axisLabelAnchor === 'start' || (!point.axisLabelAnchor && index === 0)
                      ? 'report-trend-panel__x-tick--first'
                      : '',
                    point.axisLabelAnchor === 'end' || (!point.axisLabelAnchor && index === xTicks.length - 1)
                      ? 'report-trend-panel__x-tick--last'
                      : '',
                    point.quarterBoundary ? 'report-trend-panel__x-tick--quarter' : ''
                  ].filter(Boolean).join(' ')}
                  key={point.bucket.key}
                  style={{ left: `${(point.x / 1000) * 100}%` }}
                  title={accessibleLabel}
                >
                  <strong aria-hidden="true">{label}</strong>
                  {range && <span aria-hidden="true" className="report-trend-panel__x-range">{range}</span>}
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </div>
  );
}

function RevenueTrend({
  annual = false,
  basis,
  comparisonControl = true,
  comparisonEnabled,
  onComparisonChange,
  comparisonPeriod,
  comparisonSeries = [],
  currentPeriod,
  series,
  showDateRange = false
}) {
  const intl = useIntl();
  const locale = String(intl.locale || 'ro').toLocaleLowerCase().startsWith('ro') ? 'ro' : 'en';
  const basisKey = BASIS_MESSAGE_KEYS[basis] || BASIS_MESSAGE_KEYS.without_vat;
  const basisLabel = intl.formatMessage({ id: `components.pages.reports.summary.${basisKey}` });
  const trendLabel = intl.formatMessage({ id: 'components.pages.reports.summary.revenueTrend' });
  const axisLabel = `${trendLabel}: ${basisLabel}`;
  const xAxisLabel = intl.formatMessage({ id: 'components.pages.reports.periods.chartAxis' });
  const valueAccessor = bucket => moneyMinorValue(bucket.net_sales, basis);
  const formatTick = value => formatMinorUnits({
    currency: 'RON',
    [`${basis}_minor`]: String(value)
  }, basis, locale);

  return (
    <div className="report-trend-panel" data-testid="revenue-trend">
      <div className="report-trend-panel__header">
        <h3><FormattedMessage id="components.pages.reports.summary.revenueTrend"/></h3>
        <span>{basisLabel}</span>
      </div>
      {series.length === 0 ? (
        <div className="report-trend-panel__empty"><FormattedMessage id="components.pages.reports.summary.noTrend"/></div>
      ) : (
        <TrendChart
          annual={annual}
          axisLabel={axisLabel}
          comparisonControl={comparisonControl}
          comparisonEnabled={comparisonEnabled}
          onComparisonChange={onComparisonChange}
          comparisonPeriod={comparisonPeriod}
          comparisonSeries={comparisonSeries}
          currentPeriod={currentPeriod}
          formatTick={formatTick}
          pointTitle={bucket => {
            const range = bucketDateRange(bucket);
            return `${bucket.label}${range ? ` (${range})` : ''}: ${formatMinorUnits(bucket.net_sales, basis, locale)}`;
          }}
          series={series}
          showDateRange={showDateRange}
          valueAccessor={valueAccessor}
          xAxisLabel={xAxisLabel}
        />
      )}
    </div>
  );
}

function QuantityTrend({
  annual = false,
  comparisonControl = true,
  comparisonEnabled,
  onComparisonChange,
  comparisonPeriod,
  comparisonSeries = [],
  currentPeriod,
  series,
  showDateRange = false
}) {
  const intl = useIntl();
  const locale = String(intl.locale || 'ro').toLocaleLowerCase().startsWith('ro') ? 'ro' : 'en';
  const trendLabel = intl.formatMessage({ id: 'components.pages.reports.summary.quantityTrend' });
  const axisLabel = intl.formatMessage({ id: 'components.pages.reports.summary.fulfilledQuantity' });
  const xAxisLabel = intl.formatMessage({ id: 'components.pages.reports.periods.chartAxis' });
  const valueAccessor = bucket => quantityIntegerValue(
    bucket.fulfilled_quantity && bucket.fulfilled_quantity.net
  );
  const nonNegativeValueAccessor = bucket => {
    const value = valueAccessor(bucket);
    return value !== null && value < 0n ? 0n : value;
  };

  return (
    <div className="report-trend-panel" data-testid="quantity-trend">
      <div className="report-trend-panel__header">
        <h3>{trendLabel}</h3>
        <span>{axisLabel}</span>
      </div>
      {series.length === 0 ? (
        <div className="report-trend-panel__empty"><FormattedMessage id="components.pages.reports.summary.noTrend"/></div>
      ) : (
        <TrendChart
          annual={annual}
          axisLabel={axisLabel}
          className="report-trend-panel__line--quantity"
          comparisonControl={comparisonControl}
          comparisonEnabled={comparisonEnabled}
          onComparisonChange={onComparisonChange}
          comparisonPeriod={comparisonPeriod}
          comparisonSeries={comparisonSeries}
          currentPeriod={currentPeriod}
          formatTick={value => formatQuantity(value, locale)}
          pointTitle={bucket => {
            const quantity = bucket.fulfilled_quantity || {};
            const range = bucketDateRange(bucket);
            return `${bucket.label}${range ? ` (${range})` : ''}: ${formatQuantity(nonNegativeValueAccessor(bucket), locale)} (${formatQuantity(quantity.purchased, locale)} / ${formatQuantity(quantity.direct_allocated, locale)})`;
          }}
          series={series}
          showDateRange={showDateRange}
          scaleFallback={0n}
          valueAccessor={nonNegativeValueAccessor}
          xAxisLabel={xAxisLabel}
        />
      )}
    </div>
  );
}

function CountTrend({
  annual = false,
  color,
  currentPeriod,
  secondary,
  series,
  seriesLabelId,
  testId,
  titleId,
  valueAccessor
}) {
  const intl = useIntl();
  const locale = String(intl.locale || 'ro').toLocaleLowerCase().startsWith('ro') ? 'ro' : 'en';
  const title = intl.formatMessage({ id: titleId });
  const seriesLabel = intl.formatMessage({ id: seriesLabelId || titleId });
  const secondaryLabel = secondary
    ? intl.formatMessage({ id: secondary.labelId })
    : null;
  const xAxisLabel = intl.formatMessage({ id: 'components.pages.reports.periods.chartAxis' });
  const exactValue = bucket => quantityIntegerValue(valueAccessor(bucket));

  return (
    <div className="report-trend-panel report-trend-panel--overview" data-testid={testId}>
      <div className="report-trend-panel__header"><h3>{title}</h3></div>
      {!Array.isArray(series) || series.length === 0 ? (
        <div className="report-trend-panel__empty"><FormattedMessage id="components.pages.reports.summary.noTrend"/></div>
      ) : (
        <TrendChart
          annual={annual}
          axisLabel={seriesLabel}
          className="report-trend-panel__line--quantity"
          color={color}
          comparisonControl={false}
          comparisonEnabled={false}
          currentPeriod={currentPeriod}
          formatTick={value => formatQuantity(value, locale)}
          overview
          pointTitle={bucket => `${bucket.label || bucket.key || '—'} · ${seriesLabel}: ${formatQuantity(exactValue(bucket), locale)}`}
          secondaryColor={secondary && secondary.color}
          secondaryLabel={secondaryLabel}
          secondaryPointTitle={secondary && (bucket => `${bucket.label || bucket.key || '—'} · ${secondaryLabel}: ${formatQuantity(quantityIntegerValue(secondary.valueAccessor(bucket)), locale)}`)}
          secondaryValueAccessor={secondary && (bucket => quantityIntegerValue(secondary.valueAccessor(bucket)))}
          scaleFallback={0n}
          series={series}
          showDateRange
          valueAccessor={exactValue}
          xAxisLabel={xAxisLabel}
        />
      )}
    </div>
  );
}

export {
  buildChartScale,
  buildPoints,
  ComparisonControl,
  CountTrend,
  QuantityTrend,
  RevenueTrend,
  sampleAxisPoints
};
